import { UserPermission } from '@/core/models/user-permission';
import { TranslateService } from '@ngx-translate/core';
import { TileService } from '@/core/services/open-api/tile.service';
import {
    getApiNoteFilterValue,
    UserFilter,
    AnalyzeFilters,
    TeamFilterOption,
    TeamFilterOptionTypes,
} from '../analyze-filters';
import { AnalyzeComponent } from '../analyze.component';
import { ActivityDetailVmMapper } from './activity-detail-vm-mapper';
import {
    ActivityDetailVm,
    ActivitiesMetadataForm,
    GroupFilterType,
    ActivityFiltersFormPageHack,
    ActivitySortField,
    ActivityFiltersForm,
    FilteredActivitiesResponse,
} from '@/core/models/activity';
import { ActivityService } from '@/core/services/open-api/activity.service';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ChangeDetectorRef,
    viewChildren,
} from '@angular/core';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { UserService } from '@/core/services/open-api/user.service';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { LoadingService } from '@/core/services/loading.service';
import { Layer } from '@/core/models/layer';
import { LayerService } from '@/core/services/open-api/layer.service';

import { CurrentUser } from '@/core/models/user';
import { ColumnKey, ColumnMappingService, ColumnVisibilityCache } from '@/core/services/column-mapping.service';
import { ReportSettings, RoundingType } from '@/core/models/report-settings.interface';
import { Tile } from '@/core/models/tile';
import { SafeUrl } from '@angular/platform-browser';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import { DataUtils } from '@/core/utils/data-utils';
import { ImageUtilsService } from '@/core/services/utils/image-utils.service';
import { LogoService } from '@/core/services/logo.service';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { SelectOption } from '@/core/types/angular-primeng-api';
import { HttpErrorResponse } from '@angular/common/http';
import { TableLazyLoadEvent, TableRowSelectEvent } from 'primeng/table';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { AvailableIntegrations, IntegrationConfig } from '@/core/models/IntegrationEntity';
import { CreateInvoiceComponent } from '@/home/analyze/analyze-detail/create-invoice/create-invoice.component';
import { Logo } from '@/core/models/logo-types';
import { ToastService } from '@/core/services/toast.service';
import { ReportSettingsService } from '@/core/services/report-settings/report-settings.service';
import { ReportSettingDefaults } from '@/core/services/report-settings/report-setting-defaults';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { PageDataState } from '@/common/shared/shared-types';
import { ExcelExporter } from './exports/excel-exporter';
import { CsvExporter } from './exports/csv-exporter';
import { PdfExporter } from './exports/pdf-exporter';

type ColumnDefinition = {
    field: string;
    header: string;
};

@Component({
    selector: 'app-analyze-detail',
    templateUrl: './analyze-detail.component.html',
    styleUrls: ['./analyze-detail.component.scss'],
    standalone: false,
})
export class AnalyzeDetailComponent implements OnInit, OnDestroy {
    @Input() allUsers: UserFilter[] = [];
    // contains both selected user and group filters
    @Input({ required: true }) filters!: AnalyzeFilters;
    @Input({ required: true }) filterSubmit$!: Observable<AnalyzeFilters>;
    @Output() onHasDataChanged = new EventEmitter<boolean>();
    @Output() invoiceTotalTime: string = '';
    @ViewChild('roundingOptionsOverlay') roundingOptionsOverlay: Popover | undefined;
    @ViewChild('confirmPanel') confirmPanel: Popover | undefined;
    activities: ActivityDetailVm[] = []; // the activities to be displayed
    hasActivities: boolean = false; // true when the user / account has activies independent of current filters
    // copy to serve as the ground truth for the filter
    originalActivities: ActivityDetailVm[] = [];
    selectedActivities = new Set<ActivityDetailVm>();
    selectedActivity?: ActivityDetailVm;
    showAddEditDialog = false;
    uploadedCustomLogoUrl: SafeUrl | null = null;
    defaultLogoBase64: Logo = null;
    isNewActivityMode = false;
    areAllActivitiesSelected = false;
    areAllActivitiesLocked = false;
    totalDuration: string = '';
    subTotalDuration: string = '';
    originalTotalDurationSeconds: number = 0;
    licenseStatePro = false;
    isAdminOrManager = false;
    isAdmin = false;
    currentUserId?: number;
    currentUser?: CurrentUser;
    showReportCustomizationDialog = false;
    reportSettings!: ReportSettings;
    layers: Layer[] = [];
    cols: ColumnDefinition[] = [];
    // exportColumns: any[] = [];
    actionMenu: MenuItem[] = []; // Ticket #193
    paginationFirst: number = 0;
    paginationRows: number = 25;
    roundingEnabled: boolean = ReportSettingDefaults.defaultRoundingEnabled;
    durationRoundingUnitsOptions: SelectOption[] = ReportSettingDefaults.getRoundingUnitOptions();
    selectedDurationRoundingUnit: string = ReportSettingDefaults.getDefaultRoundingUnit();
    durationRoundingTypeOptions: SelectOption[] = ReportSettingDefaults.getRoundingTypeOptions();
    selectedDurationRoundingType: RoundingType = ReportSettingDefaults.getDefaultRoundingType();
    isRoundingIconRotated: boolean = false;
    userSelectedLogoUrl: SafeUrl | null = null;
    totalCount: number = 0;
    tiles: Tile[] = [];
    isReadyForLazyLoad = false;
    isLoading = false;
    dataState: PageDataState = 'loading';
    roundedDuration: number = 0; // seconds
    metaActivityIdList: string = '';
    sortField: ActivitySortField = 'local_start_date';
    sortOrder: number = -1; // 1 for ascending, -1 for descending (server side: other value than 1 => descending)
    allSelActAreBilled: boolean = false;
    changesSaved: boolean = false;
    readonly MAX_SELECTION = 50;
    activeIntegrations: AvailableIntegrations[] = [];
    reauthIntegrations: AvailableIntegrations[] = [];
    rateLimitedIntegrations: AvailableIntegrations[] = [];
    // container for all subscriptions, that don't need an explicit handling and shall only be unsubscribed on destroy
    private subscriptions = new Subscription();
    private initMgr: InitializationManager;
    private columnVisibilityCache: ColumnVisibilityCache;

    constructor(
        private readonly activityService: ActivityService,
        private readonly toastService: ToastService,
        public readonly userService: UserService,
        private readonly tileService: TileService,
        private readonly analyzeComponent: AnalyzeComponent, // TODO remove this ref
        private readonly loadingService: LoadingService,
        private readonly layerService: LayerService,
        private readonly translate: TranslateService,
        private readonly columnMappingService: ColumnMappingService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly confirmationService: ConfirmationService,
        private readonly imageUtilsService: ImageUtilsService,
        private readonly logoService: LogoService,
        private readonly integrationService: IntegrationService,
        private readonly reportSettingsService: ReportSettingsService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
        this.columnVisibilityCache = new ColumnVisibilityCache(this.columnMappingService);
    }

    getBulkEditButtonLabel(): string {
        let totalTime = 0;
        this.selectedActivities.forEach((a) => {
            totalTime += a.durationSeconds;
        });
        // TODO add ({{selectedActivitiesTotalTime}}) to i18n label value when export and invoice generation supports
        //      to export only selected time entries, see comment in #652
        const baseText = this.translate.instant('edit_activity_bulk_selection_label', {
            selectedActivitiesCount: this.selectedActivities.size,
            selectedActivitiesTotalTime: this.getDurationFormatted(totalTime, 'seconds'),
        });

        if (this.selectedActivities.size >= this.MAX_SELECTION) {
            return `${baseText} ${this.translate.instant('max_reached_suffix')}`;
        }
        return baseText;
    }

    onUserLogoSelectionUrlChange(newUserLogoUrl: SafeUrl | null): void {
        this.userSelectedLogoUrl = newUserLogoUrl;
        this.changeDetector.detectChanges();
    }

    /**
     * Updates the total duration based on the updated duration(based on user rounding)
     */
    private updateTotalDuration(totalDuration?: number, roundedDuration?: number): void {
        const totDuration = totalDuration ?? this.originalTotalDurationSeconds;
        const roundedDur = roundedDuration ?? this.roundedDuration;
        if (!this.reportSettings.roundingEnabled) {
            this.totalDuration = this.getDurationFormatted(totDuration, 'microseconds');
        } else {
            this.totalDuration = this.getDurationFormatted(roundedDur, 'microseconds');
        }
        this.invoiceTotalTime = this.totalDuration;
    }

    private getDurationFormatted(totalDuration: number, unitType: 'seconds' | 'microseconds'): string {
        return ActivityDetailVmMapper.formatDuration(totalDuration, this.reportSettings, unitType);
    }

    onLazyLoadHandler(event: TableLazyLoadEvent): void {
        // Run the loadActivities only if the table is initialized or if the event is triggered by pagination
        if (this.isReadyForLazyLoad) {
            void this.loadActivities(event);
        } else {
            // Set the flag to true after the first initialization
            this.isReadyForLazyLoad = true;
        }
    }

    private async loadActivities(event: TableLazyLoadEvent): Promise<void> {
        return this.onPageChange(event);
    }

    refreshActivities(totalDuration?: number, roundedDuration?: number): void {
        ActivityDetailVmMapper.updateVmActivities(this.activities, this.reportSettings, this.originalActivities);
        this.recalcAllLocked();
        // after activities are updated, update the total duration
        this.updateTotalDuration(totalDuration, roundedDuration);
    }

    private getNoteFilterValue(): boolean | string | undefined {
        return getApiNoteFilterValue(this.filters.filterDescription, this.filters.isNoteAvailable);
    }

    private createActivityMetaRequestDto(): ActivityFiltersForm {
        const userIds = this.getUserIds();
        const startDate = DateRangeHelper.getStartDate(this.filters.dateRange);
        const endDate = DateRangeHelper.getEndDate(this.filters.dateRange);

        return {
            startDate,
            endDate,
            tiles: SharedFunctions.getTilesDtoFromFilters(
                this.filters.selectedLayer1Filter,
                this.filters.selectedLayer2Filter,
                this.filters.selectedLayer3Filter,
            ),
            note: this.getNoteFilterValue(),
            billed: this.filters.isBilled,
            userIds,
            groups: this.getGroups(),
        };
    }

    private updateMetaData(activityMeta: ActivitiesMetadataForm): void {
        this.originalTotalDurationSeconds = DataUtils.toNumber(activityMeta.totalDuration);
        this.roundedDuration = DataUtils.toNumber(activityMeta.roundedDuration);

        const duration = this.reportSettings.roundingEnabled ? this.roundedDuration : this.originalTotalDurationSeconds;

        this.totalDuration = ActivityDetailVmMapper.formatDuration(duration, this.reportSettings, 'microseconds');
        this.refreshActivities(activityMeta.totalDuration, activityMeta.roundedDuration);

        this.invoiceTotalTime = this.totalDuration;
        this.totalCount = activityMeta.totalCount;

        this.metaActivityIdList = activityMeta.idList;

        this.changeDetector.detectChanges();
    }

    private async loadAndRefreshMetaData(showLoading = true, pageNumber?: number, pageSize?: number): Promise<void> {
        if (showLoading) {
            this.loadingService.setLoadingState('data-loading', this.constructor.name);
        }
        await Promise.allSettled([this.load(pageNumber, pageSize), this.refreshActivityMetaData()]);
        this.calculateSelection();
        if (showLoading) {
            this.loadingService.setLoadingState('complete', this.constructor.name);
        }
    }

    // Call this to re-fetch meta data
    private async refreshActivityMetaData(): Promise<void> {
        if (!this.reportSettings) {
            console.warn('refreshActivityMetaData() - reportSettings not set', new Error().stack);
            return;
        }
        const activityRequestDto = this.createActivityMetaRequestDto();
        const activityMeta: ActivitiesMetadataForm = await firstValueFrom(
            this.activityService.fetchActivitiesMeta(activityRequestDto),
        );
        this.updateMetaData(activityMeta);
    }

    /**
     * Used by invoice creation component to refresh activities after invoice creation
     * if time entries were marked as billed.
     */
    public async onActivitiesRefreshRequested(): Promise<void> {
        await this.onPageChange({
            first: this.paginationFirst,
            rows: this.paginationRows,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
        });
    }

    /**
     * The operations in this method are not blocking the ready state of the component.
     */
    private async initInBackground(): Promise<void> {
        this.defaultLogoBase64 = await this.logoService.getDefaultLogo();
    }

    ngOnInit(): void {
        void this.initInBackground();

        this.subscriptions.add(
            this.loadingService.isLoading$.subscribe((isLoading) => {
                if (isLoading) {
                    this.dataState = 'loading';
                } else if (this.activities.length > 0) {
                    this.dataState = 'has-data';
                } else if (this.hasActivities) {
                    this.dataState = 'empty-result';
                } else {
                    this.dataState = 'no-data';
                }
            }),
        );
        this.initMgr
            .addDependency(this.integrationService.integrations$, {
                name: 'integrations$',
                callback: (integrations: IntegrationConfig[]) => {
                    this.activeIntegrations = integrations.filter((i) => i.status === 'active').map((i) => i.service);
                    this.reauthIntegrations = integrations
                        .filter((i) => i.status === 'active' && i.authStatus === 'invalid')
                        .map((i) => i.service);
                    this.rateLimitedIntegrations = integrations
                        .filter((i) => i.status === 'active' && i.authStatus === 'rate-limited')
                        .map((i) => i.service);
                },
            })
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (curUser: CurrentUser) => {
                    this.currentUser = curUser;
                    this.currentUserId = curUser.id;
                    this.licenseStatePro = curUser.isPro();
                    this.isAdminOrManager = curUser.isAdminOrManager();
                    this.isAdmin = curUser.isAdmin();
                    this.isReadyForLazyLoad = false; // TODO hotfix for weird data flow and events...
                    // TODO check why this is needed...
                    this._initLogoDeferred(curUser);
                },
            })
            .addDependency(this.tileService.tiles$, {
                name: 'tiles$',
                callback: (tiles: Tile[]) => {
                    this.tiles = tiles;
                },
            })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers: Layer[]) => {
                    this.layers = layers;
                },
            })
            .addDependency(this.filterSubmit$, {
                name: 'filterSubmit$',
                callback: (filters: AnalyzeFilters) => {
                    this.filters = filters;
                    // TODO implement a proper fix here
                    // Hotfix: As the loading of the new data always resets the checkboxes (selected items),
                    //         the resulting subTotalTime is always zero / empty.
                    //         A recalculation will not properly work at this moment as the data is not yet reset.
                    //         A proper reliable data flow should be implemented here.
                    this.subTotalDuration = '';
                },
            })
            .addDependency(this.reportSettingsService.currentReportSettings$, {
                name: 'currentReportSettings$',
                callback: (settings: ReportSettings) => {
                    this.reportSettings = settings;
                    this.selectedDurationRoundingUnit = settings.durationRoundingUnit
                        ? '' + settings.durationRoundingUnit
                        : ReportSettingDefaults.getDefaultRoundingUnit();
                    this.selectedDurationRoundingType =
                        settings.durationRoundingType ?? ReportSettingDefaults.getDefaultRoundingType();
                    this.roundingEnabled = settings.roundingEnabled;

                    // TODO if only report settings have changed, a full data reload is not needed,
                    //      instead an update is sufficent <-- performance optimization
                },
            })
            .onInit(async () => {
                await this.loadAndRefreshMetaData(false);
            })
            .onChange(async () => {
                await this.loadAndRefreshMetaData(false);
            })
            .initialize();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    async fetchFilteredActivities(
        pageNumber: number,
        pageSize: number,
        sortField: ActivitySortField,
        sortOrder: number,
    ): Promise<FilteredActivitiesResponse> {
        const activityRequestDto = this.getActivityRequestDto(pageNumber, pageSize);
        const activities = await this.activityService.getFilteredActivitiesAsync(
            activityRequestDto,
            sortField,
            sortOrder,
        );
        if (activities.totalCount > 0) {
            this.hasActivities = true;
        } else {
            const totalActivityCount = await this.activityService.getActivityCountAsync();
            this.hasActivities = totalActivityCount > 0;
        }
        return activities;
    }

    private tableState = {
        paginationFirst: 0,
        activityIds: '', // IDs of current page, cannot be used for comparison, if page is not the same.
        activityCount: 0,
    };

    private storeTableState(): void {
        this.tableState.paginationFirst = this.paginationFirst;
        this.tableState.activityIds = this.metaActivityIdList;
        this.tableState.activityCount = this.totalCount;
    }

    private restoreTableState(): void {
        const tableDataIsEqual = this.tableState.activityIds === this.metaActivityIdList;
        const tableCountIsEqual = this.tableState.activityCount === this.totalCount;
        console.debug(`restoreTableState(): countEqual=${tableCountIsEqual}, dataEqual=${tableDataIsEqual}`);

        if (!tableDataIsEqual) {
            // reset table, different data has been loaded.
            this.paginationFirst = 0;
            this.sortField = 'local_start_date';
            this.sortOrder = -1;
        } else {
            this.paginationFirst = this.tableState.paginationFirst;
        }
    }

    private _initLogoDeferred(user: CurrentUser): void {
        this.logoService.clearLegacyCache();

        this.subscriptions.add(
            this.logoService.getLogo(user.id).subscribe({
                next: (logo: Logo) => {
                    this.uploadedCustomLogoUrl = logo;
                },
                error: () => {
                    this.uploadedCustomLogoUrl = null;
                    this.toastService.error('standard_error', 'standard_failure_logo_fetch');
                },
            }),
        );
    }

    private async load(pageNumber: number = 0, pageSize: number = 25): Promise<void> {
        this.isReadyForLazyLoad = false;

        this.storeTableState();

        if (pageSize !== this.paginationRows) {
            this.paginationRows = pageSize;
        }

        const filteredActivitiesResponse = await this.fetchFilteredActivities(
            pageNumber,
            pageSize,
            this.sortField,
            this.sortOrder,
        );

        // TODO: when activities are paged the ActivityDetailVmMapper.mapToVms() needs to be called with the paged activities
        this.activities = ActivityDetailVmMapper.mapToVms(
            filteredActivitiesResponse.activities,
            this.reportSettings,
            this.analyzeComponent.usersFilterOptions,
        );
        this.recalcAllLocked();
        // deep copy to serve as the ground truth for the filter
        this.originalActivities = ActivityDetailVmMapper.deepCopy(this.activities);

        // render the table based on the report settings values from the user
        if (this.licenseStatePro) {
            this.refreshActivities();
        }

        this.onHasDataChanged.emit(this.activities.length > 0);
        this.updateTotalDuration();
        this.selectedActivities = new Set<ActivityDetailVm>();

        this.restoreTableState();

        await this.populateTableColumns();
        this.isReadyForLazyLoad = false;
    }

    // Based on changesSaved to avoid resetting when the page reloads after a change like edit or new activity
    updateValueForSavedChanges<T>(currentValue: T, newValue: T | undefined): T {
        return this.changesSaved ? currentValue : (newValue ?? currentValue);
    }

    private async onPageChange(event: TableLazyLoadEvent): Promise<void> {
        // this.oncancel(); is run because when the user paginates the table then the next
        // paginated page also has all activities selected, if the user has not dismissed the dialog
        this.onCancel();

        this.isLoading = true;
        this.paginationFirst = this.updateValueForSavedChanges(this.paginationFirst, event.first);
        this.paginationRows = this.updateValueForSavedChanges(this.paginationRows, event.rows!);
        this.sortField = this.updateValueForSavedChanges(this.sortField, event.sortField) as ActivitySortField;
        this.sortOrder = this.updateValueForSavedChanges(this.sortOrder, event.sortOrder!);

        const pageSize = this.changesSaved ? this.paginationRows : event.rows!;
        const sortField = this.changesSaved ? this.sortField : (event.sortField! as ActivitySortField);
        const sortOrder = this.changesSaved ? this.sortOrder : event.sortOrder!;

        const activityDto = await this.fetchFilteredActivities(this.paginationFirst, pageSize, sortField, sortOrder);

        this.activities = ActivityDetailVmMapper.mapToVms(
            activityDto.activities,
            this.reportSettings,
            this.analyzeComponent.usersFilterOptions,
        );
        this.recalcAllLocked();
        this.originalActivities = ActivityDetailVmMapper.deepCopy(this.activities);

        // Update checkbox state based on whether this.areAllActivitiesSelected is true or false
        const selectedActivityIds = new Set(
            [...this.selectedActivities].map((activity: ActivityDetailVm) => activity.id),
        );

        this.activities.forEach((activity) => {
            activity.isChecked = this.areAllActivitiesSelected || selectedActivityIds.has(activity.id);
        });

        await this.refreshActivityMetaData();

        // Stop loading state after data is processed
        this.changeDetector.detectChanges();
        this.isLoading = false;
        this.changesSaved = false;
    }

    /**
     *
     * @param activityDetailVm ActivityDetailVm
     * @param checked boolean
     * Used for individual activity selection via checkbox
     */
    select(activityDetailVm: ActivityDetailVm, checked: boolean): void {
        this.areAllActivitiesSelected = false;
        activityDetailVm.isChecked = checked;
        if (checked) {
            this.addActivity(activityDetailVm);
        } else {
            this.removeActivity(activityDetailVm);
        }
        this.allSelActAreBilled = this.areAllBilled(this.selectedActivities);
    }

    // Helper function to add an activity to the list of selected ones
    // Based on id, to avoid accidental false neg using the object reference in the set
    addActivity(activity: ActivityDetailVm): void {
        const exists = [...this.selectedActivities].some((a) => a.id === activity.id);
        if (!exists) {
            this.selectedActivities.add(activity);
            this.calculateSelection();
        }
    }

    // Helper function to remove an activity to the list of selected ones
    removeActivity(activity: ActivityDetailVm): void {
        const activityToRemove = [...this.selectedActivities].find((a) => a.id === activity.id);
        if (activityToRemove) {
            this.selectedActivities.delete(activityToRemove);
            this.calculateSelection();
        }
    }

    private calculateSelection(): void {
        let duration: number = 0;
        this.selectedActivities.forEach((activity) => {
            // note reportSettings rounding logic is already applied on the view model before
            duration += activity.durationSeconds;
        });
        this.subTotalDuration =
            duration > 0 ? ActivityDetailVmMapper.formatDuration(duration, this.reportSettings, 'seconds') : '';
    }

    // Handles bulk activity selection
    selectAll(checked: boolean, _event: Event | undefined): void {
        this.activities.forEach((activity) => {
            if (activity.lock?.locked && !activity.lock?.userCanOverride) {
                return;
            }
            activity.isChecked = checked;
            if (checked) {
                this.addActivity(activity);
            } else {
                this.removeActivity(activity);
            }
        });

        if (!checked) {
            this.selectedActivities.clear();
            this.confirmPanel?.hide();
            this.areAllActivitiesSelected = false;
            this.changeDetector.detectChanges();
        }
        // feat: limit the number of selected activities to 50
        if (this.selectedActivities.size > 50) {
            const excessActivities = [...this.selectedActivities].slice(50);
            excessActivities.forEach((activity) => {
                this.selectedActivities.delete(activity);
                activity.isChecked = false;
            });
        }

        this.allSelActAreBilled = this.areAllBilled(this.selectedActivities);
    }

    private areAllBilled(activitySet: Set<ActivityDetailVm>): boolean {
        return [...activitySet].every((activity) => activity.billed);
    }

    /**
     * Confirmation popup dialog for bulk selection
     * Allows user to either bulk select all activities in the entire table or just the current page
     */
    async confirmBulkSelection(panel: Popover): Promise<void> {
        this.isLoading = true;
        const activityRequestDto = this.getActivityRequestDto();

        try {
            const getAllFilteredActivities = await firstValueFrom(
                this.activityService.getFilteredActivities(activityRequestDto, this.sortField, this.sortOrder),
            );

            const fetAct = ActivityDetailVmMapper.mapToVms(
                getAllFilteredActivities.activities,
                this.reportSettings,
                this.analyzeComponent.usersFilterOptions,
            );

            // update the selected activities with the new activities because all have been selected
            this.selectedActivities.clear();
            fetAct.forEach((activity) => {
                activity.isChecked = true;
                this.addActivity(activity);
            });

            this.areAllActivitiesSelected = true;
        } catch (error) {
            console.error('Error fetching activities', error);
        } finally {
            this.isLoading = false;
            panel.hide();
        }
    }

    onCancel(): void {
        this.areAllActivitiesSelected = false;
        this.confirmPanel?.hide();
    }

    addNewActivity(): void {
        this.isNewActivityMode = true;
        this.showAddEditDialog = true;
        this.selectedActivity = undefined;
        this.resetActCheckboxSelection();
    }

    getLayerName(layerIndex: number): string {
        return this.layers.find((layer) => layer.index === layerIndex)?.name ?? '';
    }

    // TODO this is very inefficient, calling methods in templates is not recommended
    //      refactor to pre-calculate this and update on change of dependencies
    // Note: The cache helps, but is still significantly slower than pre-calculated values
    public isColumnVisible(key: ColumnKey): boolean {
        return this.columnVisibilityCache.isColumnVisible(
            key,
            this.licenseStatePro,
            this.isAdminOrManager,
            this.reportSettings,
        );
    }

    public onSaved(_activityId: number): void {
        this.changesSaved = true;
        if (this.activities.length === 0) {
            void this.loadAndRefreshMetaData();
        } else {
            void this.onActivitiesRefreshRequested();
        }
        this.selectedActivity = undefined;
    }

    public onDeleted(activityId: number): void {
        if (this.activities.length === 1 && this.activities[0].id === activityId) {
            void this.loadAndRefreshMetaData();
        }
        this.selectedActivity = undefined;
    }

    public onAddEditDialogVisibilityChange(show: boolean): void {
        if (show === false) {
            this.selectedActivity = undefined;
        }
    }

    public onRoundingEnabledChange(isButtonClicked: boolean = false): void {
        // if the button was clicked, the state needs to be toggled manually
        // if the change is caused by the toggle switch, this.roundingEnabled is already udpated by the binding
        if (isButtonClicked) {
            this.roundingEnabled = !this.roundingEnabled;
        }
        this.reportSettings.roundingEnabled = this.roundingEnabled;
        this.reportSettingsService.updateReportSettings({ roundingEnabled: this.roundingEnabled }).subscribe({
            next: () => {
                this.toastService.success('standard_success', 'standard_success_saved');
            },
            error: (_error: HttpErrorResponse) => {
                this.toastService.error('standard_error', 'profile_profilesavingerror');
            },
        });
        // TODO - check if this is needed!
        this.refreshActivities();
    }

    public applyRoundingSettings(): void {
        if (!this.licenseStatePro) {
            return;
        }
        // TODO refactor this!
        this.reportSettings.durationRoundingType = this.selectedDurationRoundingType;
        this.reportSettings.durationRoundingUnit = Number(this.selectedDurationRoundingUnit);

        this.isLoading = true;
        this.roundingOptionsOverlay?.hide();

        this.reportSettingsService
            .updateReportSettings({
                durationRoundingType: this.selectedDurationRoundingType,
                durationRoundingUnit: Number(this.selectedDurationRoundingUnit),
            })
            .subscribe({
                next: async () => {
                    this.toastService.success('standard_success', 'standard_success_saved');
                    await this.refreshActivityMetaData();
                    this.isLoading = false;
                },
                error: (_error: HttpErrorResponse) => {
                    this.toastService.error('standard_error', 'profile_profilesavingerror');
                    this.isLoading = false;
                },
            });
    }

    public openRoundingSettings(event: Event): void {
        if (!this.licenseStatePro) {
            return;
        }
        // if open and clicked again, then close
        if (this.roundingOptionsOverlay?.overlayVisible) {
            this.roundingOptionsOverlay?.hide();
            return;
        }
        this.roundingOptionsOverlay?.show(event);
        if (!this.roundingEnabled) {
            this.roundingEnabled = true;
            this.onRoundingEnabledChange();
        }
    }

    onRoundingOverlayShow(): void {
        this.isRoundingIconRotated = true;
    }

    onRoundingOverlayHide(): void {
        this.isRoundingIconRotated = false;
    }

    resetActCheckboxSelection(): void {
        // reset checkbox state for all selected avtivities and also select all checkbox
        this.areAllActivitiesSelected = false;
        this.selectedActivities.clear();
        // demark activity.checked
        this.activities.forEach((activity) => {
            activity.isChecked = false;
        });
    }

    onRowSelect(selectedRow: TableRowSelectEvent<ActivityDetailVm>): void {
        if (this.currentUser && this.currentUser.hasPermission(UserPermission.ACTIVITY_EDIT)) {
            this.selectedActivity = Array.isArray(selectedRow.data) ? selectedRow.data[0] : selectedRow.data;
            this.showAddEditDialog = true;
            this.isNewActivityMode = false;
            this.resetActCheckboxSelection();
        }
    }

    hasReadTeamPermission(): boolean {
        return !!(this.currentUser && this.currentUser.hasPermission(UserPermission.ACTIVITY_READ_TEAM));
    }

    onClick(value: string): void {
        if (value === 'delete') {
            this.confirmDeletion();
        }
        if (value === 'billed') {
            this.updateBilledOnSelectedActivities();
        }
    }

    updateBilledOnSelectedActivities(): void {
        this.loadingService.setLoading(true, this.constructor.name);

        const selectedActivities = Array.from(this.selectedActivities)
            .filter((activity): activity is ActivityDetailVm => activity !== undefined)
            .map((a) => a.id);

        const billed = !this.allSelActAreBilled;

        const afterUpdate = (): void => {
            this.selectedActivities = new Set<ActivityDetailVm>();
            this.areAllActivitiesSelected = false;
            void this.loadAndRefreshMetaData(true, this.paginationFirst, this.paginationRows);
        };

        this.activityService.billedEditBulk(selectedActivities, billed).subscribe({
            next: () => {
                afterUpdate();
            },
            error: () => {
                afterUpdate();
            },
        });
    }

    confirmDeletion(): void {
        const confrimMessage = this.translate.instant('activities_delete_confirm', {
            selectedCount: this.selectedActivities.size,
        });

        this.confirmationService.confirm({
            header: this.translate.instant('delete_group_confirm_header'),
            icon: 'pi pi-exclamation-triangle',
            message: confrimMessage,
            acceptLabel: this.translate.instant('standard_yes'),
            rejectLabel: this.translate.instant('standard_no'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                this.deleteSelectedActivities();
            },
            reject: () => {},
        });
    }

    deleteSelectedActivities(): void {
        this.loadingService.setLoading(true, this.constructor.name);

        const activityIds = Array.from(this.selectedActivities).map((a) => a.id);

        this.activityService.deleteBulk(activityIds).subscribe({
            next: () => {
                this.paginationFirst = 0;
                this.selectedActivities = new Set<ActivityDetailVm>();
                this.areAllActivitiesSelected = false;
                void this.loadAndRefreshMetaData(true, this.paginationFirst, this.paginationRows);
            },
            error: () => {
                this.paginationFirst = 0;
                this.selectedActivities = new Set<ActivityDetailVm>();
                this.areAllActivitiesSelected = false;
                void this.loadAndRefreshMetaData(true, this.paginationFirst, this.paginationRows);
                this.toastService.error('standard_error', 'activities_bulk_delete_error');
            },
        });
    }

    // TODO legacy logo url logic looks very defensive, data flow and exchange with ReportCustomizationComponent should be optimized!
    private getLogoUrl(): string {
        const logoSelection = this.reportSettings.logoOption;

        // set the user selected logo url based on the logoOption
        if (logoSelection === 'default') {
            this.userSelectedLogoUrl = this.defaultLogoBase64;
        } else if (logoSelection === 'custom') {
            this.userSelectedLogoUrl = this.uploadedCustomLogoUrl;
        } else {
            this.userSelectedLogoUrl = this.uploadedCustomLogoUrl ? this.uploadedCustomLogoUrl : this.defaultLogoBase64;
        }

        if (this.userSelectedLogoUrl) {
            return this.userSelectedLogoUrl.toString();
        }
        return this.defaultLogoBase64 ? this.defaultLogoBase64.toString() : '';
    }

    // Export PDF
    exportPdf = async (): Promise<void> => {
        const activities = await this.getSelectedActivitiesForExport();

        const filename = `timeBuzzer_${this.filters.dateRange[0]?.toLocaleDateString()}-${this.filters.dateRange[1]?.toLocaleDateString()}.pdf`;

        const pdfExporter = new PdfExporter(
            this.translate,
            this.columnMappingService,
            this.imageUtilsService,
            this.layers,
            this.reportSettings,
            this.currentUser!,
        );

        const duration = this.subTotalDuration ? this.subTotalDuration : this.totalDuration;
        await pdfExporter.export(duration, this.filters.dateRange, activities, this.getLogoUrl(), filename);
    };
    public exportExcel = async (): Promise<void> => {
        const allActivitiesWithoutPagination = await this.getSelectedActivitiesForExport();

        const excelExporter = new ExcelExporter(
            this.translate,
            this.columnMappingService,
            this.reportSettings,
            this.layers,
            this.currentUser!,
        );
        const fileName =
            'timeBuzzer_' +
            `${this.filters.dateRange[0]?.toLocaleDateString()}-${this.filters.dateRange[1]?.toLocaleDateString()}` +
            ExcelExporter.EXCEL_FILE_EXTENSION;
        await excelExporter.export(allActivitiesWithoutPagination, fileName);
    };

    private async populateTableColumns(): Promise<void> {
        const translations = await firstValueFrom(
            this.translate.get([
                'activities_user',
                'activities_note',
                'activities_date',
                'activities_time',
                'activities_duration',
                'activities_billed',
            ]),
        );

        const colsLayer: ColumnDefinition[] = [];
        colsLayer.push({
            field: 'layer1',
            header: this.getLayerName(1),
        });
        if (this.layers[1]?.visible) {
            colsLayer.push({
                field: 'layer2',
                header: this.getLayerName(2),
            });
        }
        if (this.layers[2]?.visible) {
            colsLayer.push({
                field: 'layer3',
                header: this.getLayerName(3),
            });
        }

        this.cols = [
            {
                field: 'userName',
                header: translations['activities_user'],
            },
            ...colsLayer,
            { field: 'note', header: translations['activities_note'] },
            { field: 'date', header: translations['activities_date'] },
            { field: 'time', header: translations['activities_time'] },
            {
                field: 'duration',
                header: translations['activities_duration'],
            },
            {
                field: 'billed',
                header: translations['activities_billed'],
            },
        ];
    }

    /**
     * Opens the report customization modal
     */
    openReportCustomization(): void {
        this.showReportCustomizationDialog = true;
    }

    /**
     * Fetch activities for export
     * This includes the full month or whatever range of activities without pagination
     */
    async fetchActivitiesForExport(): Promise<ActivityDetailVm[]> {
        const activityRequestDto = this.getActivityRequestDto();

        try {
            const activitiesDto = await firstValueFrom(
                this.activityService.getFilteredActivities(activityRequestDto, this.sortField, this.sortOrder),
            );

            const fetAct = ActivityDetailVmMapper.mapToVms(
                activitiesDto.activities,
                this.reportSettings,
                this.analyzeComponent.usersFilterOptions,
            );

            // Use the existing updateVmActivities function to apply rounding
            ActivityDetailVmMapper.updateVmActivities(fetAct, this.reportSettings, fetAct);

            return fetAct;
        } catch (error) {
            console.error('Error fetching activities:', error);
            throw error;
        }
    }

    /**
     * Fetches the most recent activities according to filters and filters by user selection,
     * if the user has selected entries in the table.
     */
    private async getSelectedActivitiesForExport(): Promise<ActivityDetailVm[]> {
        let activities = await this.fetchActivitiesForExport();
        if (this.selectedActivities.size > 0) {
            const idSet = new Set<number>();
            this.selectedActivities.forEach((activity) => idSet.add(activity.id));
            activities = activities.filter((activity) => idSet.has(activity.id));
        }
        return activities;
    }

    /**
     * Custom export to csv function
     */
    public exportCsv = async (): Promise<void> => {
        const activities = await this.getSelectedActivitiesForExport();
        const csvExporter = new CsvExporter(this.columnMappingService, this.reportSettings, this.layers);
        csvExporter.export(activities, `timeBuzzer_${this.getCsvFileName()}`);
    };

    /**
     * Gets the name of the csv file
     * @returns a string with the name of the csv file
     */
    getCsvFileName(): string {
        const from = this.filters.dateRange[0]?.toLocaleDateString();
        const to = this.filters.dateRange[1]?.toLocaleDateString();
        if (from && to) {
            return `timeBuzzer_${from}-${to}`;
        }
        return 'timeBuzzer';
    }

    // /// Consolidated getters for report formats /////

    getReportDateFormat(): string {
        return this.reportSettings.dateFormat;
    }

    getReportDateTimeFormat(): string {
        return this.reportSettings.dateFormat + ' ' + this.reportSettings.timeFormat;
    }

    // TODO what style is better? Have the member variable here where it is used or at the top?
    private readonly invoiceComponents = viewChildren(CreateInvoiceComponent);

    openInvoiceCreationModal(serviceId: string): void {
        const component = this.invoiceComponents().find((c) => c.serviceIdentifier === serviceId);
        if (component) {
            void component.openCreateInvoiceModal();
        }
    }

    // /////////////// getters for filters //////////////////

    // TODO: This logic should not be in this component as it is an internal logic of the analyze component, that defines the filters
    private getTeamFilterOptionsFilteredByType(type: TeamFilterOptionTypes): TeamFilterOption[] {
        return (
            this.filters.selectedTeamFilter?.filter((filterOption: TeamFilterOption) => filterOption?.type === type) ??
            []
        );
    }

    private getUserIds(): number[] {
        const filterOptions = this.getTeamFilterOptionsFilteredByType('members');
        let userIds = filterOptions.map((filterOption) => filterOption.id);
        if (userIds.length === 0) {
            userIds = this.allUsers.map((u) => u.id);
        }
        return userIds;
    }

    // TODO: Simplify group filters to only transmit IDs (like for users) and not objects with id and name
    private getGroups(): GroupFilterType[] {
        const filterOptions = this.getTeamFilterOptionsFilteredByType('groups');
        return filterOptions.map((filterOption: TeamFilterOption) => {
            const name = 'name' in filterOption && typeof filterOption.name === 'string' ? filterOption.name : '';
            return {
                id: filterOption.id,
                name,
            };
        });
    }

    public getTimezoneSwitchText(activity: ActivityDetailVm): string {
        const introMsg = this.translate.instant('activities_time_shift_text');
        const diffStr = this.translate.instant('activities_offset_time_difference');
        const startStr = this.translate.instant('activities_start');
        const endStr = this.translate.instant('activities_end');

        const start =
            activity.start.format(this.getReportDateTimeFormat()) + ` (UTC ${activity.start.getApiUtcOffset()})`;
        const end = activity.end.format(this.getReportDateTimeFormat()) + ` (UTC ${activity.end.getApiUtcOffset()})`;
        const hoursDiff = activity.start.getOffsetDiff(activity.end, 'hours');
        const sign = hoursDiff > 0 ? '+' : '';

        const dates = `<table><tbody><tr><td>${startStr}: </td><td>${start}</td></tr><tr><td>${endStr}: </td><td>${end}</td></tr></tbody></table>`;
        return `${introMsg}<br>${dates}<br>${diffStr}: ${sign}${hoursDiff}h`;
    }

    private recalcAllLocked(): void {
        if (this.activities && this.activities.length > 0) {
            const lockCount = this.activities.filter((activity) => {
                if (!activity.lock) {
                    return false;
                }
                return activity.lock.locked && !activity.lock.userCanOverride;
            }).length;
            if (lockCount === this.activities.length) {
                this.areAllActivitiesLocked = true;
                return;
            }
        }
        this.areAllActivitiesLocked = false;
    }

    public getActivityIdsForExport(): number[] | undefined {
        let activityIds: number[] | undefined = undefined;
        if (this.selectedActivities && this.selectedActivities.size > 0) {
            activityIds = Array.from(this.selectedActivities).map((a) => a.id);
        } else if (DataUtils.isNotEmptyString(this.metaActivityIdList)) {
            activityIds = this.metaActivityIdList.split(',').map((id) => parseInt(id, 10));
        } else if (this.activities && this.activities.length > 0) {
            if (this.activities.length === this.totalCount) {
                activityIds = this.activities.map((activity) => activity.id);
            } else {
                console.warn('getActivityIdsForExport() - fallback to acitivites caused incomplete result!');
            }
        }
        return activityIds;
    }

    public getSelectedActivityIds(): number[] | undefined {
        if (this.selectedActivities && this.selectedActivities.size > 0) {
            return Array.from(this.selectedActivities).map((a) => a.id);
        }
        return undefined;
    }

    // ////////// getters for request dtos /////////////

    private getActivityRequestDto(
        pageNumber: number = 0,
        pageSize: number = ActivityService.ACTIVITY_MAX,
    ): ActivityFiltersFormPageHack {
        const userIds = this.getUserIds();
        const startDate = DateRangeHelper.getStartDate(this.filters.dateRange);
        const endDate = DateRangeHelper.getEndDate(this.filters.dateRange);

        // Populate time rounding options
        this.durationRoundingUnitsOptions = ReportSettingDefaults.getRoundingUnitOptions();

        // Populate rounding type options
        this.durationRoundingTypeOptions = ReportSettingDefaults.getRoundingTypeOptions();

        return {
            startDate,
            endDate,
            pageNumber,
            pageSize,
            tiles: SharedFunctions.getTilesDtoFromFilters(
                this.filters.selectedLayer1Filter,
                this.filters.selectedLayer2Filter,
                this.filters.selectedLayer3Filter,
            ),
            note: this.getNoteFilterValue(),
            billed: this.filters.isBilled,
            userIds: userIds,
            groups: this.getGroups(),
        };
    }
}
